import { isBrowser } from '@/lib/utils';

// The app's default time zone — mirrors the accounts.time_zone_name DB default.
export const DEFAULT_TIME_ZONE = {
  name: 'Pacific Time (US & Canada)',
  ianaId: 'America/Los_Angeles',
} as const;

export const BROWSER_LOCAL_TIME_ZONE = 'browser';

export function getBrowserTimeZoneId(): string | undefined {
  if (!isBrowser()) return undefined;
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || undefined;
  } catch {
    return undefined;
  }
}

const PREFERRED_US_TIME_ZONE_NAMES = [
  'Pacific Time (US & Canada)',
  'Mountain Time (US & Canada)',
  'Central Time (US & Canada)',
  'Eastern Time (US & Canada)',
];

export function detectTimeZoneName(
  options: ReadonlyArray<{ value: string; ianaIds?: string[] }>,
  browserTimeZoneId = getBrowserTimeZoneId(),
): string {
  if (!browserTimeZoneId) return DEFAULT_TIME_ZONE.name;

  const browserCity = timeZoneCity(browserTimeZoneId);
  const matches = options.filter((option) => option.ianaIds?.includes(browserTimeZoneId));
  const match =
    matches.find((option) => option.value === browserCity) ??
    matches.find((option) => option.ianaIds?.[0] === browserTimeZoneId) ??
    matches.find((option) => timeZoneCity(option.ianaIds?.[0] ?? '') === browserCity) ??
    matches.find((option) => PREFERRED_US_TIME_ZONE_NAMES.includes(option.value)) ??
    matches.find((option) => option.value === timeZoneCity(option.ianaIds?.[0] ?? '')) ??
    matches[0];
  return match?.value ?? DEFAULT_TIME_ZONE.name;
}

const TIME_ZONE_SHORT_LABEL_OVERRIDES: Record<string, string> = {
  'Asia/Kolkata': 'IST',
};

const TIME_ZONE_SHORT_LABEL_LOCALES = [
  'en-US',
  'en-AU',
  'en-IE',
  'en-GB',
  'en-NZ',
  'en-ZA',
  'en-SG',
  'en-HK',
  'en-CA',
] as const;

function resolveIntlTimeZone(timeZone?: string | null): string | undefined {
  if (timeZone === BROWSER_LOCAL_TIME_ZONE) return undefined;
  return timeZone || DEFAULT_TIME_ZONE.ianaId;
}

function timeZoneCity(timeZone: string): string {
  const parts = timeZone.split('/');
  return parts[parts.length - 1]?.replace(/_/g, ' ') || timeZone;
}

function formatUtcOffsetLabel(date: Date, timeZone: string): string {
  try {
    const timeZoneName = new Intl.DateTimeFormat('en-US', {
      timeZone,
      timeZoneName: 'shortOffset',
    })
      .formatToParts(date)
      .find((part) => part.type === 'timeZoneName')?.value;

    const offset = timeZoneName?.replace('GMT', 'UTC');
    if (!offset || offset === 'UTC') return 'UTC+00:00';

    const match = offset.match(/^UTC([+-])(\d{1,2})(?::(\d{2}))?$/);
    if (!match) return offset;

    const [, sign, hours, minutes = '00'] = match;
    return `UTC${sign}${hours.padStart(2, '0')}:${minutes}`;
  } catch {
    return '';
  }
}

function isOffsetLabel(label?: string): boolean {
  return Boolean(label?.match(/^(?:GMT|UTC)[+-]/));
}

function formatIntlShortTimeZoneLabel(date: Date, timeZone: string): string | undefined {
  for (const locale of TIME_ZONE_SHORT_LABEL_LOCALES) {
    const label = new Intl.DateTimeFormat(locale, {
      timeZone,
      timeZoneName: 'short',
    })
      .formatToParts(date)
      .find((part) => part.type === 'timeZoneName')?.value;

    if (label && !isOffsetLabel(label)) return label;
  }

  return undefined;
}

function offsetMinutesForInstant(date: Date, timeZone: string): number | null {
  const offsetLabel = formatUtcOffsetLabel(date, timeZone);
  const match = offsetLabel.match(/^UTC([+-])(\d{2}):(\d{2})$/);
  if (!match) return null;

  const [, sign, hours, minutes] = match;
  const absoluteMinutes = Number(hours) * 60 + Number(minutes);

  return sign === '-' ? -absoluteMinutes : absoluteMinutes;
}

function localInputToTimeZoneInstant(value: string, timeZone: string): Date | null {
  const match = value.match(/^(\d{4})-(\d{2})-(\d{2})(?:T(\d{2}):(\d{2})(?::(\d{2}))?)?$/);
  if (!match) return null;

  // Date-only values anchor to noon: never inside a DST transition window.
  const [, year, month, day, hour = '12', minute = '00', second = '00'] = match;
  const localInputAsUtc = Date.UTC(
    Number(year),
    Number(month) - 1,
    Number(day),
    Number(hour),
    Number(minute),
    Number(second),
  );
  let instant = new Date(localInputAsUtc);
  const offsetMinutes = offsetMinutesForInstant(instant, timeZone);
  if (offsetMinutes === null) return null;

  instant = new Date(localInputAsUtc - offsetMinutes * 60_000);
  const correctedOffsetMinutes = offsetMinutesForInstant(instant, timeZone);
  if (correctedOffsetMinutes !== null && correctedOffsetMinutes !== offsetMinutes) {
    instant = new Date(localInputAsUtc - correctedOffsetMinutes * 60_000);
  }

  return instant;
}

export function localInputToTimeZoneIsoString(
  value?: string | null,
  timeZone?: string | null,
): string | null {
  if (!value) return null;

  if (timeZone === BROWSER_LOCAL_TIME_ZONE) {
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? null : date.toISOString();
  }

  const resolvedTimeZone = resolveIntlTimeZone(timeZone);
  if (!resolvedTimeZone) return null;

  return localInputToTimeZoneInstant(value, resolvedTimeZone)?.toISOString() ?? null;
}

export function formatTimeZoneDisplayName(timeZone?: string | null): string {
  const resolvedTimeZone = resolveIntlTimeZone(timeZone);
  if (!resolvedTimeZone) return 'Browser timezone';

  const date = new Date();
  const city = timeZoneCity(resolvedTimeZone);

  try {
    const timeZoneName = new Intl.DateTimeFormat('en-US', {
      timeZone: resolvedTimeZone,
      timeZoneName: 'longGeneric',
    })
      .formatToParts(date)
      .find((part) => part.type === 'timeZoneName')?.value;

    if (timeZoneName && !timeZoneName.includes(resolvedTimeZone)) {
      return `${timeZoneName} (${city})`;
    }
  } catch {
    // Fall through to location-based label.
  }

  return `${city} Time`;
}

export function formatTimeZoneDisplayDetails(timeZone?: string | null): string {
  const resolvedTimeZone = resolveIntlTimeZone(timeZone);
  if (!resolvedTimeZone) return 'Browser timezone';

  const offset = formatUtcOffsetLabel(new Date(), resolvedTimeZone);
  return [formatTimeZoneDisplayName(resolvedTimeZone), offset, resolvedTimeZone]
    .filter(Boolean)
    .join(' - ');
}

export function formatTimeZoneShortLabel(
  value?: string | number | Date | null,
  timeZone?: string | null,
): string {
  const resolvedTimeZone = resolveIntlTimeZone(timeZone);
  const date = value ? (value instanceof Date ? value : new Date(value)) : new Date();
  const safeDate = Number.isNaN(date.getTime()) ? new Date() : date;
  if (!resolvedTimeZone) {
    return (
      new Intl.DateTimeFormat('en-US', { timeZoneName: 'short' })
        .formatToParts(safeDate)
        .find((part) => part.type === 'timeZoneName')?.value ?? ''
    );
  }

  const override = TIME_ZONE_SHORT_LABEL_OVERRIDES[resolvedTimeZone];
  if (override) return override;

  try {
    return (
      formatIntlShortTimeZoneLabel(safeDate, resolvedTimeZone) ||
      formatUtcOffsetLabel(safeDate, resolvedTimeZone)
    );
  } catch {
    return formatUtcOffsetLabel(safeDate, resolvedTimeZone);
  }
}

export function formatTimeZoneShortLabelForLocalInput(
  value?: string | null,
  timeZone?: string | null,
): string {
  const resolvedTimeZone = resolveIntlTimeZone(timeZone);
  if (!value || !resolvedTimeZone) return formatTimeZoneShortLabel(value, timeZone);

  const date = localInputToTimeZoneInstant(value, resolvedTimeZone);
  if (!date) return resolvedTimeZone;

  return formatTimeZoneShortLabel(date, resolvedTimeZone);
}

function siteDateTimeFormat(
  options: Intl.DateTimeFormatOptions,
  timeZone?: string | null,
): Intl.DateTimeFormat {
  try {
    return new Intl.DateTimeFormat('en-US', {
      ...options,
      timeZone: resolveIntlTimeZone(timeZone),
    });
  } catch {
    return new Intl.DateTimeFormat('en-US', { ...options, timeZone: DEFAULT_TIME_ZONE.ianaId });
  }
}

const SITE_DATE_FORMAT_OPTIONS: Intl.DateTimeFormatOptions = {
  year: 'numeric',
  month: 'long',
  day: 'numeric',
};

export function formatSiteDate(value: string | number | Date, timeZone?: string | null): string {
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return 'Invalid date';

  return siteDateTimeFormat(SITE_DATE_FORMAT_OPTIONS, timeZone).format(date);
}

const SITE_MONTH_DAY_FORMAT_OPTIONS: Intl.DateTimeFormatOptions = { month: 'long', day: 'numeric' };
const SITE_TIME_FORMAT_OPTIONS: Intl.DateTimeFormatOptions = { hour: 'numeric', minute: '2-digit' };

export function formatSiteDateTime(
  value: string | number | Date,
  timeZone?: string | null,
): string {
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return 'Invalid date';

  const monthDay = siteDateTimeFormat(SITE_MONTH_DAY_FORMAT_OPTIONS, timeZone).format(date);
  const time = siteDateTimeFormat(SITE_TIME_FORMAT_OPTIONS, timeZone).format(date);
  return `${monthDay}, ${time}`;
}

export function formatDateInputValueForTimeZone(
  value: string | number | Date,
  timeZone?: string | null,
): string {
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return '';

  try {
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone: resolveIntlTimeZone(timeZone),
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    }).formatToParts(date);

    const year = parts.find((part) => part.type === 'year')?.value;
    const month = parts.find((part) => part.type === 'month')?.value;
    const day = parts.find((part) => part.type === 'day')?.value;

    if (year && month && day) return `${year}-${month}-${day}`;
  } catch {
    return '';
  }

  return '';
}

export function formatDateTimeInputValueForTimeZone(
  value: string | number | Date,
  timeZone?: string | null,
): string {
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return '';

  try {
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone: resolveIntlTimeZone(timeZone),
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hourCycle: 'h23',
    }).formatToParts(date);

    const lookup = (type: string) => parts.find((part) => part.type === type)?.value;
    const year = lookup('year');
    const month = lookup('month');
    const day = lookup('day');
    const hour = lookup('hour');
    const minute = lookup('minute');

    if (year && month && day && hour && minute) {
      return `${year}-${month}-${day}T${hour}:${minute}`;
    }
  } catch {
    return '';
  }

  return '';
}

export function startOfTomorrowInputValue(
  value: string | number | Date = new Date(),
  timeZone?: string | null,
): string {
  const datePart = formatDateInputValueForTimeZone(value, timeZone);
  const [year = '', month = '', day = ''] = datePart.split('-');
  const yearNumber = Number(year);
  const monthNumber = Number(month);
  const dayNumber = Number(day);
  if (!yearNumber || !monthNumber || !dayNumber) return '';

  const nextDate = new Date(Date.UTC(yearNumber, monthNumber - 1, dayNumber + 1));
  const nextYear = nextDate.getUTCFullYear();
  const nextMonth = String(nextDate.getUTCMonth() + 1).padStart(2, '0');
  const nextDay = String(nextDate.getUTCDate()).padStart(2, '0');

  return `${nextYear}-${nextMonth}-${nextDay}T00:00`;
}
